import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, Loader } from '@lms/ui';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { SearchInput } from '@/components/SearchInput';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { gradebookStudentHref } from '@/lib/course-admin-navigation';
import {
  buildColumnGroups,
  columnSpan,
  countCompleted,
  type GradebookColumnGroup,
  type GradebookColumnSource,
  type GradebookCompletionItem,
} from '@/lib/gradebook-columns';
import { studentProfilePath } from '@/lib/user-detail-navigation';
import { gradeCellPercentage, gradeStatusBadge, summarizeStudent, type SummaryEntry } from './gradebook-status';

interface GradeCell {
  user: { id: string; name: string; email: string };
  score?: number | null;
  status?: string;
  percentage?: number | null;
}

interface GradebookItem extends GradebookColumnSource {
  grades?: GradeCell[];
}

interface GradebookData {
  course?: { id: string; name: string; code?: string };
  passingThreshold?: number;
  students?: { id: string; name: string; email: string }[];
  items?: GradebookItem[];
  completionItems?: GradebookCompletionItem[];
}

const PAGE_SIZE = 10;
const OPEN_SECTIONS_PREFIX = 'gradebook-open-sections:';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function gradeStatusVariant(status: string): 'success' | 'danger' | 'secondary' {
  if (status === 'Aprobado') return 'success';
  if (status === 'Reprobado') return 'danger';
  return 'secondary';
}

function formatPercent(value: number, partial = false): string {
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return partial ? `${text}%*` : `${text}%`;
}

function formatCompletedAt(value: string | null | undefined): string {
  if (!value) return 'Realizada';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Realizada';
  return date.toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readOpenSections(courseId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${OPEN_SECTIONS_PREFIX}${courseId}`);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

function groupMatches(group: GradebookColumnGroup<GradebookItem>, query: string): boolean {
  if (group.type === 'activities') {
    return (
      matchesSearch(group.sectionTitle, query) ||
      group.activities.some((activity) => matchesSearch(activity.title, query))
    );
  }
  return matchesSearch(group.item.title, query);
}

export function GradebookShowPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const location = useLocation();
  const { hasAnyPermission } = usePermissions();
  const canViewProfile = hasAnyPermission(PERMISSIONS.USUARIOS_VER, PERMISSIONS.ESTUDIANTES_PERFIL_VER);
  const profileFrom = `${location.pathname}${location.search}`;
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openSections, setOpenSections] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!courseId) return;
    setOpenSections(readOpenSections(courseId));
  }, [courseId]);

  function persistOpenSections(next: Set<string>) {
    setOpenSections(next);
    if (!courseId) return;
    localStorage.setItem(`${OPEN_SECTIONS_PREFIX}${courseId}`, JSON.stringify([...next]));
  }

  const { data: courseMeta } = useQuery({
    queryKey: ['courses', courseId],
    queryFn: () => api<{ name: string; category?: { id: string } }>(`/courses/${courseId}`),
    enabled: Boolean(courseId),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['gradebook', courseId],
    queryFn: () => api<GradebookData>(`/grades/courses/${courseId}`),
    enabled: Boolean(courseId),
  });

  const items = data?.items ?? [];
  const completionItems = data?.completionItems ?? [];

  const activitySectionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of items) {
      if (item.kind === 'h5p' && item.sectionId) ids.add(item.sectionId);
    }
    for (const row of completionItems) ids.add(row.sectionId);
    return [...ids];
  }, [items, completionItems]);

  const groups = useMemo(() => {
    const all = buildColumnGroups(items, completionItems, openSections);
    if (!search.trim()) return all;
    const matched = all.filter((group) => groupMatches(group, search));
    return matched.length > 0 ? matched : all;
  }, [items, completionItems, openSections, search]);

  const anyOpen = groups.some((group) => group.type === 'activities' && group.open);
  const tableColumns = groups.reduce((total, group) => total + columnSpan(group), 4);

  const students = useMemo(() => {
    let list = [...(data?.students ?? [])];
    const matchesActivity =
      search.trim().length > 0 &&
      (items.some((item) => matchesSearch(item.title, search)) ||
        completionItems.some(
          (row) => matchesSearch(row.title, search) || matchesSearch(row.sectionTitle, search),
        ));
    if (search.trim() && !matchesActivity) {
      list = list.filter(
        (student) => matchesSearch(student.name, search) || matchesSearch(student.email, search),
      );
    } else if (search.trim() && matchesActivity) {
      const byStudent = list.filter(
        (student) => matchesSearch(student.name, search) || matchesSearch(student.email, search),
      );
      if (byStudent.length > 0) list = byStudent;
    }
    return list;
  }, [data?.students, search, items, completionItems]);

  const gradeMatrix = useMemo(() => {
    const matrix = new Map<string, Map<string, GradeCell>>();
    for (const item of items) {
      for (const grade of item.grades ?? []) {
        if (!matrix.has(grade.user.id)) matrix.set(grade.user.id, new Map());
        matrix.get(grade.user.id)!.set(item.id, grade);
      }
    }
    return matrix;
  }, [items]);

  const studentSummaries = useMemo(() => {
    const summaries = new Map<string, { average: number | null; partial: boolean; status: string }>();
    for (const student of students) {
      const entries: SummaryEntry[] = groups.map((group) => {
        if (group.type === 'activities') return { kind: 'h5p', grade: null };
        return {
          kind: group.type === 'informative' ? 'informative' : (group.item.kind ?? 'assessment'),
          grade: gradeMatrix.get(student.id)?.get(group.item.id) ?? null,
        };
      });
      summaries.set(student.id, summarizeStudent(entries));
    }
    return summaries;
  }, [students, groups, gradeMatrix]);

  const lastPage = Math.max(1, Math.ceil(students.length / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const pageStudents = students.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const rangeStart = students.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, students.length);
  const hasSearch = search.trim().length > 0;
  const hasModules = activitySectionIds.length > 0;
  const allExpanded = hasModules && activitySectionIds.every((id) => openSections.has(id));
  const allCollapsed = hasModules && activitySectionIds.every((id) => !openSections.has(id));

  function toggleSection(sectionId: string) {
    const next = new Set(openSections);
    if (next.has(sectionId)) next.delete(sectionId);
    else next.add(sectionId);
    persistOpenSections(next);
  }

  if (!courseId) return null;

  return (
    <CourseHubShell
      courseId={courseId}
      courseName={data?.course?.name ?? courseMeta?.name ?? 'Libro de calificaciones'}
      categoryId={courseMeta?.category?.id}
      activeTab="calificaciones"
      description="Consulta el progreso y los resultados de evaluación de los estudiantes de este curso."
    >
      {error && (
        <p className="mb-4 text-sm text-danger-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            <div className="lms-course-forum-card__head">
              <div className="lms-course-forum-card__heading">
                <h2 className="lms-course-forum-card__title">Calificaciones de los estudiantes</h2>
              </div>

              <form className="lms-course-forum-card__tools" onSubmit={(event) => event.preventDefault()}>
                <SearchInput
                  id="gradebook-search"
                  name="search"
                  value={search}
                  onChange={(value) => {
                    setSearch(value);
                    setPage(1);
                  }}
                  placeholder="Buscar estudiante o actividad..."
                  aria-label="Buscar estudiante o actividad"
                  className="lms-course-forum-card__search"
                />
                {hasModules && (
                  <div className="lms-gradebook__expand" role="group" aria-label="Columnas de actividades">
                    <button
                      type="button"
                      className="lms-gradebook__expand-btn"
                      aria-pressed={allExpanded}
                      disabled={allExpanded}
                      onClick={() => persistOpenSections(new Set(activitySectionIds))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        unfold_more
                      </span>
                      Expandir todo
                    </button>
                    <button
                      type="button"
                      className="lms-gradebook__expand-btn"
                      aria-pressed={allCollapsed}
                      disabled={allCollapsed}
                      onClick={() => persistOpenSections(new Set())}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        unfold_less
                      </span>
                      Contraer todo
                    </button>
                  </div>
                )}
                {hasSearch && (
                  <button
                    type="button"
                    className="lms-gradebook__clear"
                    onClick={() => {
                      setSearch('');
                      setPage(1);
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </form>
            </div>

            {groups.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      quiz
                    </span>
                  </div>
                  <h3>Sin actividades calificables</h3>
                  <p>Agrega evaluaciones o H5P con peso en el constructor del curso.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th
                          className="lms-gradebook__th lms-gradebook__th--sticky"
                          scope="col"
                          rowSpan={anyOpen ? 2 : undefined}
                        >
                          Estudiante
                        </th>
                        {groups.map((group) => {
                          if (group.type === 'activities') {
                            const controlsId = `gradebook-section-${group.sectionId}`;
                            return (
                              <th
                                key={group.sectionId}
                                id={group.open ? undefined : `gradebook-section-${group.sectionId}`}
                                className="lms-gradebook__th lms-gradebook__group-start lms-gradebook__group-end"
                                scope="col"
                                colSpan={group.open ? group.activities.length + 1 : undefined}
                                rowSpan={!group.open && anyOpen ? 2 : undefined}
                              >
                                <button
                                  type="button"
                                  className="lms-gradebook__module-toggle"
                                  aria-expanded={group.open}
                                  aria-controls={controlsId}
                                  onClick={() => toggleSection(group.sectionId)}
                                >
                                  {group.open
                                    ? `◂ ${group.sectionTitle}`
                                    : `${group.sectionTitle} ▸ / ${group.activities.length} actividades`}
                                </button>
                              </th>
                            );
                          }
                          return (
                            <th
                              key={group.item.id}
                              className="lms-gradebook__th"
                              scope="col"
                              rowSpan={anyOpen ? 2 : undefined}
                            >
                              <span className="lms-gradebook__th-label" title={group.item.title}>
                                {group.item.title}
                              </span>
                            </th>
                          );
                        })}
                        <th className="lms-gradebook__th" scope="col" rowSpan={anyOpen ? 2 : undefined}>
                          Promedio
                        </th>
                        <th className="lms-gradebook__th" scope="col" rowSpan={anyOpen ? 2 : undefined}>
                          Estado
                        </th>
                        <th
                          className="lms-gradebook__th lms-gradebook__th--actions"
                          scope="col"
                          rowSpan={anyOpen ? 2 : undefined}
                        >
                          Acciones
                        </th>
                      </tr>
                      {anyOpen ? (
                        <tr>
                          {groups.map((group) => {
                            if (group.type !== 'activities' || !group.open) return null;
                            return group.activities.map((activity, index) => (
                              <th
                                key={activity.id}
                                id={index === 0 ? `gradebook-section-${group.sectionId}` : undefined}
                                className={`lms-gradebook__th lms-gradebook__th--center lms-gradebook__th--short${index === 0 ? ' lms-gradebook__group-start' : ''}`}
                                scope="col"
                              >
                                <span className="lms-gradebook__th-label" title={activity.title}>
                                  {activity.title}
                                </span>
                              </th>
                            )).concat(
                              <th
                                key={`${group.sectionId}-total`}
                                className="lms-gradebook__th lms-gradebook__th--center lms-gradebook__group-end"
                                scope="col"
                              >
                                Total
                              </th>,
                            );
                          })}
                        </tr>
                      ) : null}
                    </thead>
                    <tbody>
                      {pageStudents.length === 0 ? (
                        <tr>
                          <td className="lms-gradebook__empty-cell" colSpan={tableColumns}>
                            <div className="lms-gradebook__empty">
                              <div className="lms-gradebook__empty-icon" aria-hidden="true">
                                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                                  group_off
                                </span>
                              </div>
                              <h3>No hay estudiantes matriculados</h3>
                              <p>
                                No hay estudiantes matriculados en este curso o ninguno coincide con los criterios de
                                búsqueda.
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        pageStudents.map((student) => {
                          const summary = studentSummaries.get(student.id);
                          const status = summary?.status ?? 'En progreso';
                          const detailHref = gradebookStudentHref(courseId, student.id, searchParams);
                          return (
                            <tr key={student.id}>
                              <th className="lms-gradebook__td lms-gradebook__td--sticky" scope="row">
                                <div className="lms-gradebook__student">
                                  <div className="lms-gradebook__avatar" aria-hidden="true">
                                    {initials(student.name)}
                                  </div>
                                  <Link to={detailHref} className="lms-gradebook__name">
                                    {student.name}
                                  </Link>
                                </div>
                              </th>
                              {groups.map((group) => {
                                if (group.type === 'activities') {
                                  const tally = countCompleted(completionItems, group.sectionId, student.id);
                                  const totalLabel = `${tally.done}/${tally.total}`;
                                  if (!group.open) {
                                    return (
                                      <td
                                        key={group.sectionId}
                                        className="lms-gradebook__td lms-gradebook__td--center lms-gradebook__group-start lms-gradebook__group-end"
                                      >
                                        {totalLabel}
                                      </td>
                                    );
                                  }
                                  return group.activities.map((activity, index) => {
                                    const row = completionItems.find(
                                      (item) => item.sectionItemId === activity.id && item.userId === student.id,
                                    );
                                    const done = row?.completed === true;
                                    return (
                                      <td
                                        key={activity.id}
                                        className={`lms-gradebook__td lms-gradebook__td--center${index === 0 ? ' lms-gradebook__group-start' : ''}`}
                                      >
                                        {done ? (
                                          <span className="lms-gradebook__ok" title={formatCompletedAt(row?.completedAt)} aria-label="Realizada">
                                            ✓
                                          </span>
                                        ) : (
                                          <span className="lms-gradebook__muted" title="Pendiente" aria-label="Pendiente">
                                            ○
                                          </span>
                                        )}
                                      </td>
                                    );
                                  }).concat(
                                    <td
                                      key={`${group.sectionId}-total`}
                                      className="lms-gradebook__td lms-gradebook__td--center lms-gradebook__group-end"
                                    >
                                      {totalLabel}
                                    </td>,
                                  );
                                }

                                const grade = gradeMatrix.get(student.id)?.get(group.item.id);
                                if (group.type === 'informative') {
                                  return (
                                    <td key={group.item.id} className="lms-gradebook__td">
                                      <span className="lms-gradebook__muted">—</span>
                                    </td>
                                  );
                                }
                                const percentage = gradeCellPercentage(grade);
                                const badge = percentage != null ? gradeStatusBadge(grade?.status) : null;
                                return (
                                  <td key={group.item.id} className="lms-gradebook__td">
                                    {percentage != null ? (
                                      <span className="inline-flex flex-wrap items-center justify-center gap-1.5">
                                        {formatPercent(percentage)}
                                        {badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
                                      </span>
                                    ) : (
                                      <span className="lms-gradebook__muted">
                                        {grade?.status === 'pending' ? 'Pendiente' : 'Por calificar'}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="lms-gradebook__td">
                                {summary?.average != null ? (
                                  formatPercent(summary.average, summary.partial)
                                ) : (
                                  <span className="lms-gradebook__muted">—</span>
                                )}
                              </td>
                              <td className="lms-gradebook__td">
                                <Badge variant={gradeStatusVariant(status)}>{status}</Badge>
                              </td>
                              <td className="lms-gradebook__td lms-gradebook__td--actions">
                                <div className="flex items-center justify-end gap-1">
                                  <Link
                                    to={detailHref}
                                    className="lms-gradebook__action"
                                    title="Ver calificaciones"
                                    aria-label={`Ver calificaciones de ${student.name}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      visibility
                                    </span>
                                  </Link>
                                  {canViewProfile ? (
                                    <Link
                                      to={studentProfilePath(student.id, profileFrom)}
                                      className="lms-gradebook__action"
                                      title="Ver perfil"
                                      aria-label={`Ver perfil de ${student.name}`}
                                    >
                                      <span className="material-symbols-outlined" aria-hidden="true">
                                        person
                                      </span>
                                    </Link>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {students.length > 0 ? (
                  <div className="lms-course-forum-card__footer">
                    <span className="lms-course-forum-card__meta">
                      Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de{' '}
                      <strong>{students.length}</strong> resultados
                    </span>
                    <div className="lms-course-forum-card__pager">
                      <button
                        type="button"
                        className="lms-course-forum-card__pager-btn"
                        aria-label="Página anterior"
                        disabled={currentPage <= 1}
                        onClick={() => setPage(Math.max(1, currentPage - 1))}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          chevron_left
                        </span>
                      </button>
                      <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                      <button
                        type="button"
                        className="lms-course-forum-card__pager-btn"
                        aria-label="Página siguiente"
                        disabled={currentPage >= lastPage}
                        onClick={() => setPage(Math.min(lastPage, currentPage + 1))}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          chevron_right
                        </span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </CourseHubShell>
  );
}
